import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Card, CardContent, CardHeader, Dialog, DialogContent, DialogTitle, FormControlLabel, Grid,
  IconButton, MenuItem, Switch, Tab, Table, TableBody, TableCell, TableHead, TableRow,
  Tabs, TextField, Tooltip, Typography,
} from '@mui/material'
import HistoryIcon from '@mui/icons-material/History'
import { LoadingButton } from '@mui/lab'
import { useSnackbar } from 'notistack'
import Loader from '../../common/Loader'
import MerchantSelect from './MerchantSelect'
import {
  GLOBAL_SCOPE, KNOB_CATALOG, categoryLabel, fetchKnobs, fetchKnobHistory, fetchMerchantKnobDefaults,
  isAdvancedKnob, merchantScope, saveKnob, withPaymentMode,
} from './decisionConfigService'

// `knobFilter` (optional) narrows the catalog to one product's knobs, e.g. PACE
// (group !== 'prism-iedd') on the Allocation Config page vs PRISM (group ===
// 'prism-iedd') on the Intelligent EDD page. Absent ⇒ all knobs.
// `tiered` (optional) splits the table into Frequently Adjusted / Advanced tabs
// (isAdvancedKnob). Edits persist across tabs and Save writes both in one go.
// `paymentModes` (optional, PACE only — PACE+ #3) shows COD and Prepaid columns next to
// "All orders", so the three values for a knob are set side by side. A COD / Prepaid cell
// left blank inherits the "All orders" value (shown greyed in the cell). They write to
// `<scope>:COD` / `<scope>:PREPAID`. PRISM doesn't resolve payment scopes, so its knob page
// keeps the single column.

// Column = one scope suffix. '' is the plain scope ("All orders").
const ALL = ''
const PAYMENT_COLUMNS = [
  { pm: 'COD', label: 'COD orders', note: 'blank = All orders' },
  { pm: 'PREPAID', label: 'Prepaid orders', note: 'blank = All orders' },
]

const isSet = (v) => v !== undefined && v !== null && v !== ''

// The four score weights. PACE scales them so they add up to 1 (a weight's real pull is
// its share of the sum), so the UI shows that share next to each one.
const WEIGHT_KEYS = ['w_delivery', 'w_fasr', 'w_tat', 'w_cost']

// Same bounds PACE enforces (proship rejects a save outside them; PACE clamps on read).
const rangeError = (k, raw) => {
  if (!isSet(raw)) return null
  const v = Number(raw)
  if (Number.isNaN(v)) return 'not a number'
  if (k.min != null && v < k.min) return `min ${k.min}`
  if (k.max != null && v > k.max) return `max ${k.max}`
  return null
}

// Payment-risk categories PACE assigns nightly (shared/features/merchant_category.py);
// only COD_HEAVY_RISKY and PREPAID_LED carry their own values in prod today.
const CATEGORY_TIERS = ['COD_HEAVY_RISKY', 'COD_HEAVY_HEALTHY', 'PREPAID_LED', 'MIXED', 'UNCLASSIFIED']
const CATEGORY_SWITCH = 'use_category_defaults'

// Every value column (All orders / COD / Prepaid) gets the same fixed width, in the header
// AND the body, so the three line up and don't resize with their content (a select row
// used to widen its column).
const VALUE_COL_SX = { width: 170, minWidth: 170, maxWidth: 170 }

// "0 — shadow only (shown in the explainer…)" → "0 · shadow": fits the fixed column; the
// full text stays in the open menu.
const shortOption = (k, v) => {
  const label = String((k.options.find((o) => String(o.value) === String(v)) || {}).label || v)
  const [head, rest] = label.split(' — ')
  return rest ? `${head} · ${rest.split(' ')[0]}` : head
}

export default function KnobsTab({ knobFilter, tiered = false, paymentModes = false }) {
  const { enqueueSnackbar } = useSnackbar()
  const catalog = knobFilter ? KNOB_CATALOG.filter(knobFilter) : KNOB_CATALOG
  const [tier, setTier] = useState(0)                    // 0 = frequently adjusted, 1 = advanced
  const visibleCatalog = tiered
    ? catalog.filter((k) => (tier === 1 ? isAdvancedKnob(k) : !isAdvancedKnob(k)))
    : catalog
  const [scopeType, setScopeType] = useState('global')   // 'global' | 'merchant'
  const [merchant, setMerchant] = useState(null)         // full object from MerchantSelect
  // per column (pm suffix) → key → value. `global` holds the global scope's values when a
  // merchant is selected, so an unset merchant cell can show the number it really inherits.
  const [loaded, setLoaded] = useState({})
  const [global, setGlobal] = useState({})
  const [edits, setEdits] = useState({})                 // pm → key → string input
  const [split, setSplit] = useState(false)               // show COD / Prepaid columns
  // merchant view (PACE): the merchant's category + its knob values, from PACE. null = none
  // / unknown; `failed` = PACE unreachable, so defaults below are global-only.
  const [cat, setCat] = useState({ risk: null, knobs: {}, failed: false })
  // global view (PACE): category scopes that override global for their merchants
  const [categoryOverrides, setCategoryOverrides] = useState([])   // [{ risk, knobs }]
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [history, setHistory] = useState({ open: false, key: null, rows: [] })

  const merchantId = merchant?.merchantId ?? ''
  const baseScope = scopeType === 'global' ? GLOBAL_SCOPE : merchantScope(merchantId)
  const canQuery = scopeType === 'global' || merchantId !== ''
  const columns = useMemo(
    () => [{ pm: ALL, label: paymentModes ? 'All orders' : 'Value',
      note: scopeType === 'merchant' ? 'blank = global' : 'blank = default' },
      ...(paymentModes && split ? PAYMENT_COLUMNS : [])],
    [paymentModes, split, scopeType],
  )
  const scopeOf = (pm) => withPaymentMode(baseScope, pm)

  const load = useCallback(() => {
    if (!canQuery) return
    setLoading(true)
    const toMap = (res) => {
      const map = {}
      ;(res?.data || []).forEach((k) => { map[k.key] = k.value })
      return map
    }
    const pms = paymentModes ? [ALL, 'COD', 'PREPAID'] : [ALL]
    const own = pms.map((pm) => fetchKnobs(withPaymentMode(baseScope, pm)))
    // merchant view: also read the global sets, so blanks show real inherited numbers
    const glob = scopeType === 'merchant'
      ? pms.map((pm) => fetchKnobs(withPaymentMode(GLOBAL_SCOPE, pm)))
      : []
    const defaults = scopeType === 'merchant' && paymentModes
      ? fetchMerchantKnobDefaults(merchantId)
        .then((r) => ({ risk: r?.data?.payment_risk || null, knobs: r?.data?.category_knobs || {}, failed: false }))
        .catch(() => ({ risk: null, knobs: {}, failed: true }))
      : Promise.resolve({ risk: null, knobs: {}, failed: false })
    defaults.then(setCat)
    if (scopeType === 'global' && paymentModes) {
      Promise.all(CATEGORY_TIERS.map((risk) => fetchKnobs(`category#${risk}`)
        .then((r) => ({ risk, knobs: toMap(r) })).catch(() => ({ risk, knobs: {} }))))
        .then((list) => setCategoryOverrides(list.filter((x) => Object.keys(x.knobs).length)))
    } else {
      setCategoryOverrides([])
    }
    Promise.all([...own, ...glob])
      .then((res) => {
        const mine = {}
        const theirs = {}
        pms.forEach((pm, i) => { mine[pm] = toMap(res[i]) })
        if (glob.length) pms.forEach((pm, i) => { theirs[pm] = toMap(res[pms.length + i]) })
        setLoaded(mine)
        setGlobal(theirs)
        setEdits({})
        // open the COD / Prepaid columns automatically when this scope already uses them
        if (paymentModes) {
          setSplit(Object.keys(mine.COD || {}).length > 0 || Object.keys(mine.PREPAID || {}).length > 0)
        }
      })
      .catch(() => enqueueSnackbar('Failed to load knobs', { variant: 'error' }))
      .finally(() => setLoading(false))
  }, [baseScope, scopeType, merchantId, canQuery, paymentModes, enqueueSnackbar])

  useEffect(() => { load() }, [load])

  const editOf = (pm, key) => (edits[pm] || {})[key]
  const loadedOf = (pm, key) => (loaded[pm] || {})[key]
  const setEdit = (pm, key, v) => setEdits((e) => ({ ...e, [pm]: { ...(e[pm] || {}), [key]: v } }))
  // what this cell will hold after Save: the typed value, else the stored one
  const valueOf = (pm, key) => (isSet(editOf(pm, key)) ? Number(editOf(pm, key)) : loadedOf(pm, key))
  const isChanged = (pm, key) => {
    const v = editOf(pm, key)
    return isSet(v) && Number(v) !== loadedOf(pm, key)
  }

  // What applies with NO merchant override, mirroring PACE's resolution
  // global → global:<pm> → category#<risk> → merchant → merchant:<pm> (most specific wins).
  // Is the category layer on for this view? use_category_defaults: the merchant's own value
  // (typed or saved) wins, else global, else OFF — the same rule PACE applies (opt-in).
  const categoryOn = () => {
    const own = valueOf(ALL, CATEGORY_SWITCH)
    if (isSet(own)) return Boolean(Number(own))
    const g = scopeType === 'merchant' ? (global[ALL] || {})[CATEGORY_SWITCH] : undefined
    return isSet(g) ? Boolean(Number(g)) : false
  }

  const defaultFor = (pm, k) => {
    const g = (p) => (global[p] || {})[k.key]
    const c = categoryOn() ? (cat.knobs || {})[k.key] : undefined
    if (isSet(c)) return { v: c, from: `category · ${categoryLabel(cat.risk)}` }
    if (pm !== ALL && isSet(g(pm))) return { v: g(pm), from: `global ${pm}` }
    if (isSet(g(ALL))) return { v: g(ALL), from: 'global' }
    return { v: k.default, from: 'built-in default' }
  }

  // What a blank cell falls back to.
  const inheritedFor = (pm, k) => {
    if (scopeType !== 'merchant') {
      if (pm === ALL) return { v: k.default, from: 'built-in default' }
      const all = valueOf(ALL, k.key)
      return isSet(all) ? { v: all, from: 'All orders' } : { v: k.default, from: 'built-in default' }
    }
    // merchant: a blank COD / Prepaid cell takes the merchant's own All-orders value first
    // (merchant outranks category and global), then whatever applies with no override
    if (pm !== ALL) {
      const all = valueOf(ALL, k.key)
      if (isSet(all)) return { v: all, from: 'All orders' }
    }
    return defaultFor(pm, k)
  }

  const changes = () => columns.flatMap(({ pm }) => catalog
    .filter((k) => isChanged(pm, k.key))
    .map((k) => ({ pm, k, value: Number(editOf(pm, k.key)) })))

  // value this cell resolves to (own, typed or saved, else what it inherits)
  const effectiveOf = (pm, k) => {
    const own = valueOf(pm, k.key)
    return isSet(own) ? Number(own) : Number(inheritedFor(pm, k).v)
  }
  const weightSum = (pm) => WEIGHT_KEYS.reduce((t, key) => {
    const k = catalog.find((x) => x.key === key)
    return k ? t + Math.max(0, effectiveOf(pm, k) || 0) : t
  }, 0)
  const weightShare = (pm, k) => {
    const sum = weightSum(pm)
    return sum > 0 ? Math.max(0, effectiveOf(pm, k) || 0) / sum : null
  }
  const weightWarnings = columns
    .map(({ pm, label }) => ({ label, sum: weightSum(pm) }))
    .filter(({ sum }) => sum > 0 && Math.abs(sum - 1) > 0.001)

  const save = () => {
    const bad = columns.flatMap(({ pm, label }) => catalog
      .filter((k) => isChanged(pm, k.key) && rangeError(k, editOf(pm, k.key)))
      .map((k) => `${k.label} (${label}): ${rangeError(k, editOf(pm, k.key))}`))
    if (bad.length) {
      enqueueSnackbar(`Fix before saving — ${bad.join('; ')}`, { variant: 'error' })
      return
    }
    if (!reason.trim()) {
      enqueueSnackbar('A reason is required for any change', { variant: 'warning' })
      return
    }
    const changed = changes()
    if (changed.length === 0) {
      enqueueSnackbar('No changes to save', { variant: 'info' })
      return
    }
    setSaving(true)
    Promise.all(changed.map(({ pm, k, value }) => saveKnob(scopeOf(pm), k.key, value, reason.trim())))
      .then(() => {
        enqueueSnackbar(`Saved ${changed.length} value(s)`, { variant: 'success' })
        setReason('')
        load()
      })
      // proship validates against PACE's catalog; show its reason (e.g. "above its maximum 1")
      .catch((e) => enqueueSnackbar(`Save failed: ${e?.response?.data?.error || e?.message || 'unknown error'}`,
        { variant: 'error' }))
      .finally(() => setSaving(false))
  }

  // History for every visible column of one knob, newest first, labelled by column.
  const openHistory = (key) => {
    Promise.all(columns.map(({ pm, label }) => fetchKnobHistory(scopeOf(pm), key)
      .then((res) => (res.data || []).map((r) => ({ ...r, appliesTo: label })))))
      .then((lists) => {
        const rows = lists.flat().sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)))
        setHistory({ open: true, key, rows })
      })
      .catch(() => enqueueSnackbar('Failed to load history', { variant: 'error' }))
  }

  // Count of pending (changed) edits within a tier, so edits made on the other tab aren't
  // forgotten before Save (Save writes both tiers in one go).
  const pendingInTier = (advanced) => columns.reduce((n, { pm }) => n + catalog
    .filter((k) => isAdvancedKnob(k) === advanced && isChanged(pm, k.key)).length, 0)

  const tierLabel = (name, count) => (count > 0 ? `${name} (${count})` : name)

  // merchant view: read-only column with what applies if the merchant sets nothing
  const showDefault = scopeType === 'merchant' && canQuery
  const fmtVal = (k, v) => (k.options ? shortOption(k, v) : String(v))
  const renderDefault = (k) => {
    const d = defaultFor(ALL, k)
    const byPm = split ? PAYMENT_COLUMNS.map(({ pm }) => ({ pm, d: defaultFor(pm, k) }))
      .filter((x) => x.d.v !== d.v) : []
    return (
      <Box>
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtVal(k, d.v)}</Typography>
        <Typography variant="caption" color="text.secondary" component="div">{d.from}</Typography>
        {byPm.map(({ pm, d: x }) => (
          <Typography key={pm} variant="caption" color="text.secondary" component="div">
            {pm}: {fmtVal(k, x.v)} ({x.from})
          </Typography>
        ))}
      </Box>
    )
  }

  const renderCell = (pm, k) => {
    const stored = loadedOf(pm, k.key)
    const value = editOf(pm, k.key) ?? (isSet(stored) ? String(stored) : '')
    const inh = inheritedFor(pm, k)
    const changed = isChanged(pm, k.key)
    const sx = changed ? { '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,193,7,0.15)' } } : undefined
    const hint = isSet(stored) || changed ? null : `inherits ${inh.v} · ${inh.from}`
    return k.options ? (
      <TextField select fullWidth size="small" value={value} sx={sx}
        SelectProps={{
          displayEmpty: true,
          // the closed field shows the short form ("0", "1") — the full option text is
          // too long for a fixed-width column; blank shows what it inherits
          renderValue: (v) => (isSet(v)
            ? shortOption(k, v)
            : <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>{`${inh.v} (inherited)`}</Typography>),
        }}
        onChange={(e) => setEdit(pm, k.key, e.target.value)}>
        <MenuItem value="" disabled>
          <Typography variant="caption" color="text.secondary">{hint || 'choose…'}</Typography>
        </MenuItem>
        {k.options.map((o) => (
          <MenuItem key={o.value} value={String(o.value)}>{o.label}</MenuItem>
        ))}
      </TextField>
    ) : (
      <TextField type="number" size="small" fullWidth value={value} sx={sx}
        placeholder={hint ? `${inh.v} (inherited)` : undefined}
        error={changed && !!rangeError(k, editOf(pm, k.key))}
        helperText={changed && rangeError(k, editOf(pm, k.key))
          ? rangeError(k, editOf(pm, k.key))
          : (WEIGHT_KEYS.includes(k.key) && weightShare(pm, k) != null
            ? `→ ${Math.round(weightShare(pm, k) * 100)}% of the score`
            : undefined)}
        inputProps={{ min: k.min ?? undefined, max: k.max ?? undefined, step: 'any' }}
        onChange={(e) => setEdit(pm, k.key, e.target.value)} />
    )
  }

  return (
    <Card>
      {(loading || saving) && <Loader />}
      <CardHeader title="Business knobs"
        subheader={paymentModes
          ? 'Global defaults, or per-merchant overrides (merchant wins). Optionally a different value for COD and for Prepaid orders — leave those blank to use the "All orders" value.'
          : 'Global defaults, or per-merchant overrides (merchant wins).'} />
      <CardContent>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={3}>
            <TextField select fullWidth size="small" label="Scope" value={scopeType}
              onChange={(e) => setScopeType(e.target.value)}>
              <MenuItem value="global">Global (defaults)</MenuItem>
              <MenuItem value="merchant">Merchant override</MenuItem>
            </TextField>
          </Grid>
          {scopeType === 'merchant' && (
            <Grid item xs={12} md={4}>
              <MerchantSelect value={merchant} onChange={setMerchant} />
            </Grid>
          )}
          {paymentModes && (
            <Grid item xs={12} md={3}>
              <Tooltip title="Show COD and Prepaid columns to give those orders their own value for any knob. Blank = same as All orders.">
                <FormControlLabel
                  control={<Switch size="small" checked={split} onChange={(e) => setSplit(e.target.checked)} />}
                  label={<Typography variant="body2">Different values for COD / Prepaid</Typography>} />
              </Tooltip>
            </Grid>
          )}
          <Grid item xs={12} md={2}>
            <Typography variant="caption" color="text.secondary">scope = {baseScope}{split ? ' (+ :COD / :PREPAID)' : ''}</Typography>
          </Grid>
        </Grid>

        {scopeType === 'global' && categoryOverrides.length > 0 && !categoryOn() && (
          <Alert severity="success" sx={{ mb: 1.5 }}>
            Category defaults are <b>off</b> (the default): every merchant resolves global → global COD / Prepaid →
            merchant → merchant COD / Prepaid, so a knob with no COD / Prepaid value uses the global value here. The
            category values below are kept for reference only — set <b>Use category defaults</b> to 1 to apply them.
          </Alert>
        )}
        {scopeType === 'global' && categoryOverrides.length > 0 && categoryOn() && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Some merchants don&apos;t use these global values for every knob: their payment-risk category has its own
            default, which wins over global — and over the global COD / Prepaid values (a merchant override still wins
            over both). Set <b>Use category defaults</b> back to 0 to use global values for everyone.
            {categoryOverrides.map(({ risk, knobs }) => (
              <Typography key={risk} variant="caption" component="div" sx={{ mt: 0.5 }}>
                <b>{categoryLabel(risk)}</b>:{' '}
                {Object.entries(knobs).map(([key, v]) => `${(KNOB_CATALOG.find((k) => k.key === key) || {}).label || key} ${v}`).join(' · ')}
              </Typography>
            ))}
          </Alert>
        )}

        {showDefault && paymentModes && (
          <Alert severity={cat.failed ? 'warning' : 'info'} sx={{ mb: 1.5 }}>
            {cat.failed
              ? 'Could not reach PACE for this merchant\'s category, so "Default" shows global values only — a category default may apply instead.'
              : cat.risk && Object.keys(cat.knobs || {}).length && categoryOn()
                ? <>This merchant is in the <b>{categoryLabel(cat.risk)}</b> category. With no override of its own it uses that
                    category&apos;s values for {Object.keys(cat.knobs).length} knob(s), not global — see the Default column.</>
                : cat.risk && Object.keys(cat.knobs || {}).length
                  ? <>This merchant is in the <b>{categoryLabel(cat.risk)}</b> category, but category defaults are off, so it
                      uses global (and global COD / Prepaid) values — see the Default column.</>
                : <>&ldquo;Default&rdquo; is what this merchant gets with no override of its own (global, or the built-in default).
                    Anything you set in the merchant columns replaces it.</>}
          </Alert>
        )}

        {weightWarnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            The four score weights (Delivery %, FASR %, FATA, Logistics) add up to{' '}
            {weightWarnings.map(({ label, sum }) => `${Math.round(sum * 100) / 100} (${label})`).join(', ')}, not 1.
            PACE scales them so they add up to 100% — the % under each weight is the share it actually gets.
          </Alert>
        )}

        {tiered && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
            <Tabs value={tier} onChange={(_, v) => setTier(v)}>
              <Tab label={tierLabel('Frequently Adjusted', pendingInTier(false))} />
              <Tab label={tierLabel('Advanced', pendingInTier(true))} />
            </Tabs>
          </Box>
        )}

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Knob</TableCell>
              {showDefault && (
                <TableCell sx={{ ...VALUE_COL_SX, verticalAlign: 'bottom' }}>
                  Default
                  <Typography variant="caption" color="text.secondary" component="div">if no override</Typography>
                </TableCell>
              )}
              {columns.map(({ pm, label, note }) => (
                <TableCell key={pm || 'all'} sx={{ ...VALUE_COL_SX, verticalAlign: 'bottom' }}>
                  {label}
                  {columns.length > 1 && (
                    <Typography variant="caption" color="text.secondary" component="div">{note}</Typography>
                  )}
                </TableCell>
              ))}
              <TableCell>Description</TableCell>
              <TableCell align="right">History</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleCatalog.map((k) => (
              <TableRow key={k.key} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{k.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {k.key}{k.unit ? ` · ${k.unit}` : ''} · {k.group}
                  </Typography>
                </TableCell>
                {showDefault && <TableCell sx={VALUE_COL_SX}>{renderDefault(k)}</TableCell>}
                {columns.map(({ pm }) => (
                  <TableCell key={pm || 'all'} sx={VALUE_COL_SX}>
                    {renderCell(pm, k)}
                  </TableCell>
                ))}
                <TableCell><Typography variant="caption">{k.description}</Typography></TableCell>
                <TableCell align="right">
                  <Tooltip title="Change history">
                    <IconButton size="small" onClick={() => openHistory(k.key)}><HistoryIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField fullWidth size="small" label="Reason for change (audited)" value={reason}
            onChange={(e) => setReason(e.target.value)} />
          <LoadingButton variant="contained" loading={saving} disabled={!canQuery} onClick={save}>
            Save changes{changes().length ? ` (${changes().length})` : ''}
          </LoadingButton>
        </Box>
      </CardContent>

      <Dialog open={history.open} onClose={() => setHistory({ ...history, open: false })} maxWidth="md" fullWidth>
        <DialogTitle>History · {history.key} · {baseScope}</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.length > 1 && <TableCell>Applies to</TableCell>}
                <TableCell>Value</TableCell><TableCell>Reason</TableCell>
                <TableCell>By</TableCell><TableCell>At (UTC)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.rows.map((r, i) => (
                <TableRow key={i}>
                  {columns.length > 1 && <TableCell>{r.appliesTo}</TableCell>}
                  <TableCell>{JSON.stringify(r.value)}</TableCell>
                  <TableCell>{r.reason}</TableCell>
                  <TableCell>{r.updatedBy}</TableCell>
                  <TableCell>{r.updatedAt}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

